import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import { Container, Row, Col, Table, Button, ButtonGroup } from 'reactstrap';
import PickerRow from './pickerRow';
import Icon from '../../common/containers/icon';
import SectionPanel from '../../common/containers/sectionPanel';
import entityComposition from '../../common/classes/entityComposition';
import routeHelper from '../../common/classes/routeHelper';
import { hasPermission } from '../../../utils/paths';
const postNameDuplicationWarning = notificationActions => notificationActions.error({
  message: 'Could not update, please ensure that all names are distinct.'
});
const postInvalidNameWarning = notificationActions => notificationActions.error({
  message: 'Could not update, please enter a valid picker name.'
});
const postPendingChangesWarning = notificationActions => notificationActions.error({
  message: 'Please finish editing picker values before attempting to save.'
});

// must be defined outside of the component to avoid re-rendering loops
const defaultLocationFunction = () => {};
const Picker = _ref => {
  let _ref$picker = _ref.picker,
    list = _ref$picker.list,
    indexQuery = _ref$picker.indexQuery,
    _ref$location = _ref.location,
    location = _ref$location === void 0 ? defaultLocationFunction : _ref$location,
    canView = _ref.canView,
    notificationActions = _ref.notificationActions,
    showDisabled = _ref.showDisabled,
    lang = _ref.lang,
    onHasChange = _ref.onHasChange,
    hasChange = _ref.hasChange,
    _ref$pickerActions = _ref.pickerActions,
    serverIndex = _ref$pickerActions.serverIndex,
    serverSaveAll = _ref$pickerActions.serverSaveAll,
    pickerType = _ref.pickerType;
  const _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    pickerRows = _useState2[0],
    setPickerRows = _useState2[1];
  // const [hasChange, setHasChange] = useState(false);
  const _useState3 = useState({}),
    _useState4 = _slicedToArray(_useState3, 2),
    isEditingHashMap = _useState4[0],
    setIsEditingHashMap = _useState4[1];
  // Effect queries the server for the newest
  // version of the csv list
  useEffect(() => {
    routeHelper.handleViewRedirect(canView, location);
    setPickerRows([]);
    serverIndex(_objectSpread(_objectSpread({}, indexQuery), {}, {
      table: pickerType.tableName,
      lang,
      filters: {
        disabled: showDisabled
      }
    }));
  }, [indexQuery, pickerType, serverIndex, lang, showDisabled, canView, location]);
  // Effect firest when we recieve a list and we don't have one
  // already tracked by state
  useEffect(() => {
    if (pickerRows.length === 0 && list.length > 0) {
      setPickerRows(list.sort((l, r) => l.sortOrder - r.sortOrder));
    }
  }, [list, pickerRows]);
  const updateList = modifiedList => {
    onHasChange(true);
    const result = modifiedList;
    setPickerRows(result);
  };
  const onSave = () => {
    const isCurrentlyEditing = Object.values(isEditingHashMap).some(editing => editing);
    if (isCurrentlyEditing) {
      postPendingChangesWarning(notificationActions);
      return;
    }
    serverSaveAll(pickerType.tableName, lang, showDisabled, pickerRows);
    onHasChange(false);
    setPickerRows([]);
  };
  const onEditingChange = (index, isEditing) => {
    const mapCopy = JSON.parse(JSON.stringify(isEditingHashMap));
    mapCopy[index] = isEditing;
    setIsEditingHashMap(mapCopy);
  };
  const onAdd = () => {
    const newID = uuid();
    const newPickerItem = {
      id: newID,
      name: '',
      disabled: showDisabled,
      identifier: newID,
      metaDeleted: 0,
      sortOrder: pickerRows.length > 0 ? Math.max(...pickerRows.map(row => row.sortOrder)) + 1 : 0
    };
    const newList = pickerRows.concat([newPickerItem]);
    updateList(newList);
    onEditingChange(newList.length - 1, true);
  };
  const _onTextChange = (updatedText, index) => {
    const listCopy = JSON.parse(JSON.stringify(pickerRows));
    const foundIndex = listCopy.findIndex(item => item.name === updatedText);
    if (updatedText === '') {
      postInvalidNameWarning(notificationActions);
    } else if (foundIndex !== -1 && foundIndex !== index) {
      postNameDuplicationWarning(notificationActions);
    } else {
      listCopy[index].name = updatedText;
      updateList(listCopy);
      onEditingChange(index, false);
    }
  };
  const changeOrder = (prev, curr) => {
    const listCopy = JSON.parse(JSON.stringify(pickerRows));
    const _listCopy$splice = listCopy.splice(prev, 1),
      _listCopy$splice2 = _slicedToArray(_listCopy$splice, 1),
      item = _listCopy$splice2[0];
    item.hasChange = true;
    listCopy.splice(curr, 0, item);
    updateList(listCopy);
  };
  const _onDisableToggle = (isDisabled, index) => {
    const listCopy = JSON.parse(JSON.stringify(pickerRows));
    listCopy[index].disabled = isDisabled ? 1 : 0;
    updateList(listCopy);
  };
  return /*#__PURE__*/React.createElement(Container, {
    fluid: true
  }, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    sm: 12
  }, /*#__PURE__*/React.createElement(SectionPanel, {
    isPending: hasChange,
    isSaveDisabled: !hasChange,
    name: pickerType.name,
    onBack: false,
    onSave: onSave,
    showSave: true
  }, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    sm: 12
  }, /*#__PURE__*/React.createElement(Table, null, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
    style: {
      borderTop: 0
    }
  }, "Title"), /*#__PURE__*/React.createElement("th", {
    style: {
      borderTop: 0
    }
  }, /*#__PURE__*/React.createElement(ButtonGroup, {
    className: "float-md-right"
  }, /*#__PURE__*/React.createElement(Button, {
    className: "t-button-new",
    size: "sm",
    onClick: onAdd
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "plus"
  }), " New"))))), /*#__PURE__*/React.createElement("tbody", null, pickerRows.map((row, index) => /*#__PURE__*/React.createElement(PickerRow, {
    id: row.id,
    index: index,
    onDisableToggle: isDisabled => _onDisableToggle(isDisabled, index),
    key: row.id,
    name: row.name,
    disabled: row.disabled,
    editing: isEditingHashMap[index] || false,
    sortOrder: row.sortOrder,
    onEditingStart: () => onEditingChange(index, true),
    onTextChange: updatedText => _onTextChange(updatedText, index),
    onOrderChange: (x, nextIndex) => changeOrder(x, nextIndex)
  }))))))))));
};
Picker.propTypes = {
  picker: PropTypes.shape({
    indexQuery: PropTypes.shape({}),
    list: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  onHasChange: PropTypes.func.isRequired,
  showDisabled: PropTypes.number.isRequired,
  lang: PropTypes.string.isRequired,
  hasChange: PropTypes.bool.isRequired,
  pickerActions: PropTypes.shape({
    serverIndex: PropTypes.func,
    serverSaveAll: PropTypes.func
  }).isRequired,
  location: PropTypes.func,
  canView: PropTypes.bool.isRequired,
  notificationActions: PropTypes.shape({}).isRequired,
  pickerType: PropTypes.shape({
    name: PropTypes.string,
    tableName: PropTypes.string
  }).isRequired
};
export { Picker as TestPicker };
export default entityComposition(Picker, ['picker', 'notification'], {
  canView: () => hasPermission('general', 'picker_edit')
});